import { useSession } from "next-auth/client";
import dynamic from "next/dynamic";
import Link from "next/link";
import { forwardRef, ReactNode } from "react";
import BeginnerIcon from "shared/assets/icons/BeginnerIcon";
import BellIcon from "shared/assets/icons/BellIcon";
import { Header, HeaderProps } from "shared/components/atoms/Header";
import { Logo } from "shared/components/atoms/Logo";
import tw from "twin.macro";
import { Paths } from "utils/routes";

const Button = dynamic(
  async () => (await import("shared/components/atoms/Button")).Button
);

const buttonCss = tw`h-8! text-xs! leading-none!`;

const iconButtonCss = tw`flex-center flex-col font-base text-[6px] text-center`;

const iconLabelCss = tw`font-semibold`;

const transparentHeaderCss = tw`bg-[transparent] border-[transparent]`;

interface Props extends Omit<HeaderProps, "children"> {
  disabled?: boolean;
  transparent?: boolean;
  children?: ReactNode;
}

const DefaultParts = ({ transparent = false }: Props) => {
  const [session] = useSession();
  return (
    <>
      {!session ? (
        <>
          <Link href={Paths.users.signIn.index()} passHref>
            <a tw="mr-1">
              <Button
                variant="outline"
                css={[
                  buttonCss,
                  transparent &&
                    tw`bg-[transparent]! border-white! text-white!`,
                ]}
              >
                ログイン
              </Button>
            </a>
          </Link>
          <Link href={Paths.users.signIn.index()}>
            <a>
              <Button
                tw="bg-theme!"
                css={[buttonCss, transparent && tw`bg-white! text-theme!`]}
              >
                新規登録
              </Button>
            </a>
          </Link>
        </>
      ) : (
        <>
          <Link href={Paths.guideline()} passHref>
            <a css={[iconButtonCss, iconLabelCss]}>
              <BeginnerIcon tw="mb-1" />
              ご利用ガイド
            </a>
          </Link>
          <Link href={Paths.announcements.index()} passHref>
            <a
              tw="ml-3"
              css={[
                iconButtonCss,
                iconLabelCss,
                transparent ? tw`text-white!` : tw`text-theme!`,
              ]}
            >
              <BellIcon tw="mb-1" css={transparent && tw`svg text-white`} />
              お知らせ
            </a>
          </Link>
        </>
      )}
    </>
  );
};

export const AppHeader = forwardRef<HTMLDivElement, Props>(
  ({ transparent = false, disabled, children, ...headerProps }, ref) => {
    return (
      <Header
        ref={ref}
        tw="h-14 py-0 px-4"
        css={[transparent && transparentHeaderCss]}
        {...headerProps}
      >
        <Logo color={transparent ? "white" : "theme"} disabled={disabled} />
        <div tw="flex flex-1 justify-end items-center">
          {children ? children : <DefaultParts transparent={transparent} />}
        </div>
      </Header>
    );
  }
);

AppHeader.displayName = "AppHeader";
